// The one framing function: where a map opens, and how far it zooms, to show a set of area shapes.
// Fits the bounding box of the shapes (in Web Mercator, on MapLibre's 512px world) with padding and a
// maximum zoom. Pure: no React or services.
import { COUNTRIES, DEFAULT_COUNTRY } from '../../config/countries';
import type { CountryCode, MapView } from '../../config/countries';
import { positionsOf, readShapes } from './readShape';
import type { Position, ShapeInput } from './types';

/** MapLibre's world tile size: the world is 512 * 2^zoom pixels wide. */
export const WORLD_TILE = 512;
export const MIN_ZOOM = 3;
/** The closest a map opens, however small the area. */
export const MAX_ZOOM = 14;
/** Share of the bounding box's span added on every side. */
export const BBOX_PAD = 0.15;
/** The size a map is assumed to be when its real size is not known. */
export const DEFAULT_BOX = { width: 640, height: 384 };
const MAX_LAT = 85.0511287798;

export interface FitOptions {
  /** Pixel size of the map; defaults to DEFAULT_BOX. */
  width?: number;
  height?: number;
  minZoom?: number;
  maxZoom?: number;
}

/** Longitude/latitude to Web Mercator world coordinates in 0..1 (x east, y south). */
export function lngLatToWorld([lng, lat]: Position): Position {
  const clamped = Math.max(-MAX_LAT, Math.min(MAX_LAT, lat));
  const s = Math.sin((clamped * Math.PI) / 180);
  return [(lng + 180) / 360, 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)];
}

/** Web Mercator world (0..1) back to lng/lat. */
export function worldToLngLat([x, y]: Position): Position {
  return [x * 360 - 180, (Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180) / Math.PI];
}

/**
 * Fractional zoom at which a world span (0..1 units) fits width x height pixels on a 512px world,
 * clamped to [minZoom, maxZoom]. A zero span gives maxZoom.
 */
export function fitZoom(
  spanX: number,
  spanY: number,
  width: number,
  height: number,
  minZoom = MIN_ZOOM,
  maxZoom = MAX_ZOOM,
): number {
  const need = Math.max(spanX > 0 ? spanX / Math.max(width, 1) : 0, spanY > 0 ? spanY / Math.max(height, 1) : 0);
  if (need <= 0) return maxZoom;
  return Math.max(minZoom, Math.min(maxZoom, Math.log2(1 / (WORLD_TILE * need))));
}

/**
 * The view that shows the shapes: centred on the middle of their bounding box, at the zoom at which the
 * box plus padding fits the map. Null when the shapes have no coordinates.
 */
function boundsView(shapes: readonly ShapeInput[], options: FitOptions = {}): MapView | null {
  const world = positionsOf(readShapes(shapes)).map(lngLatToWorld);
  if (world.length === 0) return null;

  const xs = world.map((p) => p[0]);
  const ys = world.map((p) => p[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const { width = DEFAULT_BOX.width, height = DEFAULT_BOX.height, minZoom, maxZoom } = options;
  const zoom = fitZoom((maxX - minX) * (1 + 2 * BBOX_PAD), (maxY - minY) * (1 + 2 * BBOX_PAD), width, height, minZoom, maxZoom);
  const [longitude, latitude] = worldToLngLat([(minX + maxX) / 2, (minY + maxY) / 2]);
  return { longitude, latitude, zoom };
}

/**
 * Where a map showing these shapes opens: on the shapes when there are any, otherwise (still loading,
 * failed, or nothing to draw) on the default view of `fallbackCountry`. A country that is not known falls
 * back to the registry default. `shapes` may be area shapes or anything readShape reads (WKT text, the
 * `{ value }` wrapper, GeoJSON), so a page can hand over the geometries it holds as they are.
 */
export function fitView(
  shapes: readonly ShapeInput[],
  fallbackCountry: CountryCode | null | undefined,
  options: FitOptions = {},
): MapView {
  return boundsView(shapes, options) ?? COUNTRIES[fallbackCountry ?? DEFAULT_COUNTRY].defaultView;
}
